import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Check, ChevronRight, Store, FileText, Package, Share2, MapPin, Search, AlertCircle, Link as LinkIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import AddressAutocomplete from '@/components/AddressAutocomplete';

const LOGO_URL = "/logo-adegga.png";

const steps = [
  { id: 1, title: 'Sua Loja', icon: Store },
  { id: 2, title: 'Template', icon: FileText },
  { id: 3, title: 'Produtos', icon: Package },
  { id: 4, title: 'Publicar', icon: Share2 }
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [store, setStore] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cepLoading, setCepLoading] = useState(false);
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  
  // Step 1 form
  const [storeForm, setStoreForm] = useState({
    name: '',
    slug: '',
    whatsapp: '',
    address: { cep: '', street: '', number: '', neighborhood: '', city: '', state: '', latitude: null, longitude: null },
    delivery_enabled: true,
    pickup_enabled: true,
    delivery_fee: { type: 'per_km', fixed_value: 5, price_per_km: 3, min_fee: 5, max_distance_km: 15 }
  });

  // Handle Google Maps address selection for store
  const handleStoreAddressSelect = (addressData) => {
    setStoreForm(prev => ({
      ...prev,
      address: {
        ...prev.address,
        cep: addressData.cep || prev.address.cep,
        street: addressData.street || '',
        number: addressData.number || '',
        neighborhood: addressData.neighborhood || '',
        city: addressData.city || '',
        state: addressData.state || '',
        latitude: addressData.latitude || null,
        longitude: addressData.longitude || null
      }
    }));
    
    if (addressData.latitude && addressData.longitude) {
      toast.success('Endereço localizado no mapa!');
    }
  };
  
  // Step 3 - Batch products
  const [batchRows, setBatchRows] = useState([
    { category: '', name: '', price: '', stock: '' },
    { category: '', name: '', price: '', stock: '' },
    { category: '', name: '', price: '', stock: '' },
    { category: '', name: '', price: '', stock: '' },
    { category: '', name: '', price: '', stock: '' }
  ]);

  const fetchData = useCallback(async () => {
    if (!token) {
      console.log('Waiting for token...');
      return;
    }
    
    try {
      // Ensure axios has the token
      const headers = { Authorization: `Bearer ${token}` };
      
      const [storeRes, templatesRes] = await Promise.all([
        axios.get(`${API}/stores/me`, { headers }).catch((err) => {
          console.error('Error fetching store:', err.response?.data || err.message);
          return null;
        }),
        axios.get(`${API}/templates`, { headers })
      ]);
      
      if (storeRes?.data) {
        setStore(storeRes.data);
        setStoreForm(prev => ({
          name: storeRes.data.name || prev.name || '',
          slug: storeRes.data.slug || '',
          whatsapp: storeRes.data.whatsapp || '',
          address: storeRes.data.address || { cep: '', street: '', number: '', neighborhood: '', city: '', state: '' },
          delivery_enabled: storeRes.data.delivery_enabled ?? true,
          pickup_enabled: storeRes.data.pickup_enabled ?? true,
          delivery_fee: storeRes.data.delivery_fee || { type: 'per_km', fixed_value: 5, price_per_km: 3, min_fee: 5, max_distance_km: 15 }
        }));
        setCurrentStep(storeRes.data.onboarding_step || 1);
        if (storeRes.data.slug) {
          setSlugAvailable(true);
        }
      }
      setTemplates(templatesRes.data);
    } catch (e) {
      console.error('Error in fetchData:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Formatar CEP com máscara (00000-000)
  const formatCep = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) {
      return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    }
    return digits;
  };

  // Buscar endereço por CEP
  const fetchAddressByCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      toast.error('Digite um CEP válido com 8 dígitos');
      return;
    }
    
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await res.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado. Verifique se o CEP está correto.');
        return;
      }
      
      setStoreForm(prev => ({
        ...prev,
        address: {
          ...prev.address,
          cep: cleanCep,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || ''
        }
      }));
      toast.success('Endereço encontrado!');
    } catch (e) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  // Gerar slug a partir do nome
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Verificar disponibilidade do slug
  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    
    setSlugChecking(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/stores/check-slug/${slug}`, { headers }).catch(() => null);
      
      if (res?.data) {
        setSlugAvailable(res.data.available);
        if (!res.data.available && res.data.suggestion) {
          toast.info(`Slug já em uso. Sugestão: ${res.data.suggestion}`);
        }
      }
    } catch (e) {
      // If endpoint doesn't exist, try public check
      try {
        await axios.get(`${API}/public/stores/${slug}`);
        setSlugAvailable(false);
      } catch (err) {
        if (err.response?.status === 404) {
          setSlugAvailable(true);
        }
      }
    } finally {
      setSlugChecking(false);
    }
  };

  // Verificar slug quando mudar
  useEffect(() => {
    const timer = setTimeout(() => {
      if (storeForm.slug && storeForm.slug !== store?.slug) {
        checkSlugAvailability(storeForm.slug);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [storeForm.slug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveStep = async (step) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/stores/me`, {
        ...storeForm,
        onboarding_step: step
      }, { headers });
    } catch (e) {
      console.error('Error saving step:', e);
    }
  };

  const handleStep1Next = async () => {
    if (!storeForm.name || !storeForm.whatsapp) {
      toast.error('Preencha o nome e WhatsApp da loja');
      return;
    }
    
    if (!storeForm.slug || storeForm.slug.length < 3) {
      toast.error('Escolha um link para sua loja (mínimo 3 caracteres)');
      return;
    }
    
    if (slugAvailable === false) {
      toast.error('Este link já está em uso. Escolha outro.');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.put(`${API}/stores/me`, {
        ...storeForm,
        onboarding_step: 2
      }, { headers });
      setStore(res.data);
      setCurrentStep(2);
      toast.success('Dados salvos!');
    } catch (e) {
      console.error('Error in handleStep1Next:', e.response?.data || e.message);
      toast.error('Erro ao salvar: ' + (e.response?.data?.detail || 'Tente novamente'));
    }
  };

  const handleStep2Next = async () => {
    if (!selectedTemplate) {
      toast.error('Selecione um template');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const storeId = store?.id || user?.store_id;
      await axios.post(`${API}/stores/${storeId}/apply-template/${selectedTemplate}`, {}, { headers });
      await saveStep(3);
      setCurrentStep(3);
      toast.success('Template aplicado!');
    } catch (e) {
      console.error('Error in handleStep2Next:', e.response?.data || e.message);
      toast.error('Erro ao aplicar template');
    }
  };

  const handleStep3Next = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    const validRows = batchRows.filter(r => r.name && r.price);
    
    if (validRows.length > 0) {
      try {
        const catRes = await axios.get(`${API}/categories`, { headers });
        const categories = catRes.data;
        const categoryMap = {};
        
        for (const row of validRows) {
          if (row.category && !categoryMap[row.category]) {
            const existing = categories.find(c => c.name.toLowerCase() === row.category.toLowerCase());
            if (existing) {
              categoryMap[row.category] = existing.id;
            } else {
              const res = await axios.post(`${API}/categories`, { name: row.category, is_visible: true }, { headers });
              categoryMap[row.category] = res.data.id;
            }
          }
        }

        const productsToCreate = validRows.map(row => ({
          name: row.name,
          category_id: categoryMap[row.category] || categories[0]?.id,
          price: parseFloat(row.price) || 0,
          stock_quantity: parseInt(row.stock) || 0,
          stock_control: true,
          is_active: true,
          description: '',
          tags: [],
          variants: [],
          addons: [],
          images: []
        }));

        if (productsToCreate.length > 0) {
          await axios.post(`${API}/products/batch`, { products: productsToCreate }, { headers });
        }
      } catch (e) {
        console.error('Error in handleStep3Next:', e);
      }
    }
    
    await saveStep(4);
    setCurrentStep(4);
  };

  const handlePublish = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/stores/me`, {
        onboarding_completed: true,
        onboarding_step: 4,
        is_active: true
      }, { headers });
      toast.success('Cardápio publicado! 🎉');
      navigate('/app/dashboard');
    } catch (e) {
      console.error('Error in handlePublish:', e.response?.data || e.message);
      toast.error('Erro ao publicar');
    }
  };

  const updateBatchRow = (index, field, value) => {
    const newRows = [...batchRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setBatchRows(newRows);
  };

  const addMoreRows = () => {
    setBatchRows([...batchRows, ...Array(5).fill({ category: '', name: '', price: '', stock: '' })]);
  };

  const progress = (currentStep / 4) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a2e]">
      {/* Header */}
      <header className="bg-[#0a0a0f] border-b border-white/5 py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="ADEGGA" className="h-8" />
          <span className="text-white/60 text-sm">Passo {currentStep} de 4</span>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-[#0a0a0f] pb-6">
        <div className="max-w-4xl mx-auto px-4">
          <Progress value={progress} className="h-2 bg-white/10" />
          <div className="flex justify-between mt-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex flex-col items-center ${
                  step.id <= currentStep ? 'text-[#ff6b00]' : 'text-white/30'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                  step.id < currentStep ? 'bg-[#ff6b00] text-white' :
                  step.id === currentStep ? 'bg-[#ff6b00]/20 border-2 border-[#ff6b00]' :
                  'bg-white/10'
                }`}>
                  {step.id < currentStep ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
                </div>
                <span className="text-xs hidden sm:block">{step.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Step 1: Store Info */}
        {currentStep === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Vamos começar!</h1>
              <p className="text-white/60">Conte-nos sobre sua loja</p>
            </div>

            <Card className="card-adegga">
              <CardContent className="p-6 space-y-4">
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Nome da Loja</label>
                  <Input
                    placeholder="Ex: Adega do João"
                    value={storeForm.name}
                    onChange={(e) => {
                      const newName = e.target.value;
                      setStoreForm(prev => ({ 
                        ...prev, 
                        name: newName,
                        slug: prev.slug || generateSlug(newName)
                      }));
                    }}
                    className="bg-[#0f0f15] border-white/10 text-white text-lg h-12"
                  />
                </div>

                <div>
                  <label className="text-sm text-white/60 mb-1 block flex items-center gap-2">
                    <LinkIcon className="w-4 h-4" /> Link da sua loja (será o endereço para seus clientes)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 text-sm whitespace-nowrap">{window.location.origin}/</span>
                    <Input
                      placeholder="minha-adega"
                      value={storeForm.slug}
                      onChange={(e) => setStoreForm({ ...storeForm, slug: generateSlug(e.target.value) })}
                      className={`bg-[#0f0f15] border-white/10 text-white flex-1 ${
                        slugAvailable === true ? 'border-green-500' : 
                        slugAvailable === false ? 'border-red-500' : ''
                      }`}
                    />
                    {slugChecking && (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#ff6b00]"></div>
                    )}
                  </div>
                  {slugAvailable === true && storeForm.slug && (
                    <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Link disponível!
                    </p>
                  )}
                  {slugAvailable === false && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Este link já está em uso. Escolha outro.
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm text-white/60 mb-1 block">WhatsApp (para receber pedidos)</label>
                  <Input
                    placeholder="11999999999"
                    value={storeForm.whatsapp}
                    onChange={(e) => setStoreForm({ ...storeForm, whatsapp: e.target.value })}
                    className="bg-[#0f0f15] border-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-white/60 mb-1 block flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Endereço da Loja (para cálculo de entrega)
                  </label>
                  
                  {/* Google Maps Autocomplete */}
                  <AddressAutocomplete
                    value={storeForm.address.street ? `${storeForm.address.street}, ${storeForm.address.number}, ${storeForm.address.neighborhood}, ${storeForm.address.city}` : ''}
                    onChange={handleStoreAddressSelect}
                    placeholder="Pesquisar endereço no Google Maps..."
                  />
                  
                  {/* Ou buscar por CEP */}
                  <div className="relative my-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/10"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-[#13131f] px-2 text-white/40">ou digite o CEP</span>
                    </div>
                  </div>
                  
                  {/* CEP com busca automática */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="CEP (ex: 01310-100)"
                      value={formatCep(storeForm.address.cep || '')}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        address: { ...storeForm.address, cep: e.target.value.replace(/\D/g, '') }
                      })}
                      className="bg-[#0f0f15] border-white/10 text-white flex-1"
                      maxLength={9}
                    />
                    <Button
                      type="button"
                      onClick={() => fetchAddressByCep(storeForm.address.cep)}
                      disabled={cepLoading || !storeForm.address.cep || storeForm.address.cep.replace(/\D/g, '').length < 8}
                      className="btn-secondary"
                    >
                      {cepLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Search className="w-4 h-4 mr-1" /> Buscar</>
                      )}
                    </Button>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input
                      placeholder="Rua/Avenida"
                      value={storeForm.address.street}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        address: { ...storeForm.address, street: e.target.value }
                      })}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                    <Input
                      placeholder="Número"
                      value={storeForm.address.number}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        address: { ...storeForm.address, number: e.target.value }
                      })}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Input
                      placeholder="Bairro"
                      value={storeForm.address.neighborhood}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        address: { ...storeForm.address, neighborhood: e.target.value }
                      })}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                    <Input
                      placeholder="Cidade"
                      value={storeForm.address.city}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        address: { ...storeForm.address, city: e.target.value }
                      })}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                    <Input
                      placeholder="Estado (SP)"
                      value={storeForm.address.state}
                      onChange={(e) => setStoreForm({
                        ...storeForm,
                        address: { ...storeForm.address, state: e.target.value }
                      })}
                      className="bg-[#0f0f15] border-white/10 text-white"
                    />
                  </div>
                  
                  {/* Coordenadas salvas */}
                  {storeForm.address.latitude && storeForm.address.longitude && (
                    <div className="text-xs text-green-400 flex items-center gap-1">
                      <Check className="w-3 h-3" /> Localização GPS salva
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between p-4 bg-[#0f0f15] rounded-lg">
                  <div>
                    <p className="text-white">Fazer entregas</p>
                  </div>
                  <Switch
                    checked={storeForm.delivery_enabled}
                    onCheckedChange={(v) => setStoreForm({ ...storeForm, delivery_enabled: v })}
                  />
                </div>

                {storeForm.delivery_enabled && (
                  <div className="p-4 bg-[#0f0f15] rounded-lg space-y-4">
                    <div>
                      <label className="text-sm text-white/60 mb-2 block">Tipo de Taxa de Entrega</label>
                      <Select
                        value={storeForm.delivery_fee.type}
                        onValueChange={(v) => setStoreForm({
                          ...storeForm,
                          delivery_fee: { ...storeForm.delivery_fee, type: v }
                        })}
                      >
                        <SelectTrigger className="bg-[#1a1a2e] border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1a2e] border-white/10">
                          <SelectItem value="per_km">Por Quilômetro (R$/km)</SelectItem>
                          <SelectItem value="fixed">Taxa Fixa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {storeForm.delivery_fee.type === 'per_km' && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs text-white/40 mb-1 block">R$ por km</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">R$</span>
                              <Input
                                type="number"
                                step="0.50"
                                value={storeForm.delivery_fee.price_per_km || 3}
                                onChange={(e) => setStoreForm({
                                  ...storeForm,
                                  delivery_fee: { ...storeForm.delivery_fee, price_per_km: parseFloat(e.target.value) || 3 }
                                })}
                                className="bg-[#1a1a2e] border-white/10 text-white pl-10"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-white/40 mb-1 block">Taxa mínima</label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">R$</span>
                              <Input
                                type="number"
                                step="0.50"
                                value={storeForm.delivery_fee.min_fee || 5}
                                onChange={(e) => setStoreForm({
                                  ...storeForm,
                                  delivery_fee: { ...storeForm.delivery_fee, min_fee: parseFloat(e.target.value) || 5 }
                                })}
                                className="bg-[#1a1a2e] border-white/10 text-white pl-10"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-white/40 mb-1 block">Máx km</label>
                            <Input
                              type="number"
                              value={storeForm.delivery_fee.max_distance_km || 15}
                              onChange={(e) => setStoreForm({
                                ...storeForm,
                                delivery_fee: { ...storeForm.delivery_fee, max_distance_km: parseFloat(e.target.value) || 15 }
                              })}
                              className="bg-[#1a1a2e] border-white/10 text-white"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-[#ff6b00]">
                          Ex: Cliente a 5km paga R$ {Math.max(storeForm.delivery_fee.min_fee || 5, 5 * (storeForm.delivery_fee.price_per_km || 3)).toFixed(2)}
                        </p>
                      </div>
                    )}

                    {storeForm.delivery_fee.type === 'fixed' && (
                      <div>
                        <label className="text-xs text-white/40 mb-1 block">Valor da taxa fixa</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">R$</span>
                          <Input
                            type="number"
                            step="0.50"
                            value={storeForm.delivery_fee.fixed_value || 5}
                            onChange={(e) => setStoreForm({
                              ...storeForm,
                              delivery_fee: { ...storeForm.delivery_fee, fixed_value: parseFloat(e.target.value) || 5 }
                            })}
                            className="bg-[#1a1a2e] border-white/10 text-white pl-10"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-[#0f0f15] rounded-lg">
                  <div>
                    <p className="text-white">Retirada no local</p>
                    <p className="text-sm text-white/40">Cliente busca na loja</p>
                  </div>
                  <Switch
                    checked={storeForm.pickup_enabled}
                    onCheckedChange={(v) => setStoreForm({ ...storeForm, pickup_enabled: v })}
                  />
                </div>
              </CardContent>
            </Card>

            <Button onClick={handleStep1Next} className="w-full btn-primary h-12 text-lg">
              Continuar <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Template */}
        {currentStep === 2 && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Escolha um template</h1>
              <p className="text-white/60">Comece com categorias e produtos prontos</p>
            </div>

            <RadioGroup value={selectedTemplate} onValueChange={setSelectedTemplate} className="space-y-4">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  className={`card-adegga cursor-pointer transition-all ${
                    selectedTemplate === template.id ? 'border-[#ff6b00] bg-[#ff6b00]/10' : ''
                  }`}
                  onClick={() => setSelectedTemplate(template.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <RadioGroupItem value={template.id} id={template.id} />
                      <div className="flex-1">
                        <Label htmlFor={template.id} className="text-lg font-bold text-white cursor-pointer">
                          {template.name}
                        </Label>
                        <p className="text-sm text-white/60">{template.description}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {template.categories?.slice(0, 4).map((cat, i) => (
                            <span key={i} className="text-xs bg-white/10 px-2 py-1 rounded text-white/60">
                              {cat.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>

            <div className="flex gap-4">
              <Button variant="ghost" onClick={() => setCurrentStep(1)} className="flex-1 text-white">
                Voltar
              </Button>
              <Button onClick={handleStep2Next} className="flex-1 btn-primary">
                Aplicar Template <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Products */}
        {currentStep === 3 && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Adicione seus produtos</h1>
              <p className="text-white/60">Modo planilha: rápido e fácil</p>
            </div>

            <Card className="card-adegga overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/60 w-[25%]">Categoria</TableHead>
                      <TableHead className="text-white/60 w-[35%]">Nome do Produto</TableHead>
                      <TableHead className="text-white/60 w-[20%]">Preço</TableHead>
                      <TableHead className="text-white/60 w-[20%]">Estoque</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batchRows.map((row, i) => (
                      <TableRow key={i} className="border-white/5">
                        <TableCell className="p-1">
                          <Input
                            placeholder="Ex: Cervejas"
                            value={row.category}
                            onChange={(e) => updateBatchRow(i, 'category', e.target.value)}
                            className="bg-[#0f0f15] border-white/10 text-white h-9"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input
                            placeholder="Nome do produto"
                            value={row.name}
                            onChange={(e) => updateBatchRow(i, 'name', e.target.value)}
                            className="bg-[#0f0f15] border-white/10 text-white h-9"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input
                            type="number"
                            placeholder="0.00"
                            value={row.price}
                            onChange={(e) => updateBatchRow(i, 'price', e.target.value)}
                            className="bg-[#0f0f15] border-white/10 text-white h-9"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input
                            type="number"
                            placeholder="0"
                            value={row.stock}
                            onChange={(e) => updateBatchRow(i, 'stock', e.target.value)}
                            className="bg-[#0f0f15] border-white/10 text-white h-9"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>

            <Button variant="ghost" onClick={addMoreRows} className="text-[#ff6b00]">
              + Adicionar mais 5 linhas
            </Button>

            <p className="text-center text-white/40 text-sm">
              Você pode pular esta etapa e adicionar produtos depois
            </p>

            <div className="flex gap-4">
              <Button variant="ghost" onClick={() => setCurrentStep(2)} className="flex-1 text-white">
                Voltar
              </Button>
              <Button onClick={handleStep3Next} className="flex-1 btn-primary">
                Continuar <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Publish */}
        {currentStep === 4 && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Pronto para publicar! 🎉</h1>
              <p className="text-white/60">Seu cardápio está quase no ar</p>
            </div>

            <Card className="card-adegga bg-[#ff6b00]/10 border-[#ff6b00]/30">
              <CardContent className="p-6 text-center">
                <Check className="w-16 h-16 text-[#ff6b00] mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">{storeForm.name || 'Sua Loja'}</h2>
                <p className="text-white/60 mb-4">
                  Clique em "Publicar" para começar a receber pedidos
                </p>
                <div className="bg-[#0f0f15] p-4 rounded-lg">
                  <p className="text-sm text-white/60">Seu link será:</p>
                  <p className="text-[#ff6b00] font-mono text-lg">
                    {window.location.origin}/{storeForm.slug || store?.slug || 'sua-loja'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <p className="text-center text-white/40 text-sm">
              Você pode adicionar fotos dos produtos depois no painel de cardápio.
            </p>

            <div className="flex gap-4">
              <Button variant="ghost" onClick={() => setCurrentStep(3)} className="flex-1 text-white">
                Voltar
              </Button>
              <Button onClick={handlePublish} className="flex-1 btn-primary h-12 text-lg">
                Publicar Cardápio 🚀
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
